import React, { useState, useEffect } from "react";
import axios from "axios";
import { X, Eye, Check, Trash2, AlertTriangle, Plus } from "lucide-react";

const API = window.location.origin;

const CATEGORIES = ["dinner", "sunset", "lunch", "daytime"];

const EDIT_FIELDS = [
  { key: "confirmation_code", label: "Kod", w: "w-32" },
  { key: "customer_name", label: "Musteri", w: "w-44" },
  { key: "customer_phone", label: "Telefon", w: "w-36" },
  { key: "adult_count", label: "Yetiskin", w: "w-16", num: true },
  { key: "child_count", label: "Cocuk", w: "w-16", num: true },
  { key: "infant_count", label: "Bebek", w: "w-16", num: true },
  { key: "menu_tipi", label: "Menu", w: "w-28" },
  { key: "kalkis", label: "Kalkis", w: "w-24" },
];

export default function ManualEntryDialog({ open, onClose, defaultDate, onConfirmed }) {
  const [step, setStep] = useState("input");
  const [date, setDate] = useState(defaultDate);
  const [time, setTime] = useState("20:30");
  const [category, setCategory] = useState("dinner");
  const [rawText, setRawText] = useState("");
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const token = localStorage.getItem("auth_token");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    if (open) {
      setStep("input"); setRawText(""); setRecords([]); setResult(null);
      setDate(defaultDate); setTime("20:30"); setCategory("dinner");
    }
  }, [open, defaultDate]);

  if (!open) return null;

  const doPreview = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/bookings/manual/preview`,
        { raw_text: rawText, booking_date: date, booking_time: time, product_category: category }, { headers });
      setRecords(data.records || []);
      setStep("preview");
    } catch (e) { console.error(e); setResult({ error: true }); }
    setLoading(false);
  };

  const editField = (idx, key, value) => {
    setRecords(prev => prev.map((r, i) => {
      if (i !== idx) return r;
      const nr = { ...r };
      if (["adult_count", "child_count", "infant_count"].includes(key)) {
        nr[key] = value === "" ? 0 : parseInt(value) || 0;
        nr.total_pax = (nr.adult_count || 0) + (nr.child_count || 0) + (nr.infant_count || 0);
      } else {
        nr[key] = value;
      }
      return nr;
    }));
  };

  const deleteRow = (idx) => setRecords(prev => prev.filter((_, i) => i !== idx));

  const doConfirm = async () => {
    if (!records.length) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/bookings/manual/confirm`,
        { booking_date: date, booking_time: time, product_category: category, bookings: records }, { headers });
      setResult(data);
      if (data.created > 0) {
        onConfirmed && onConfirmed(data);
        setTimeout(() => onClose(), 1200);
      }
    } catch (e) { console.error(e); setResult({ error: true }); }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="manual-dialog">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h2 className="text-base font-bold text-gray-800 flex items-center gap-2"><Plus className="w-4 h-4 text-blue-600" /> Manuel Rezervasyon Girisi</h2>
          <button data-testid="manual-close" onClick={onClose} className="p-1.5 rounded hover:bg-gray-100 text-gray-500"><X className="w-4 h-4" /></button>
        </div>

        {/* meta */}
        <div className="px-5 py-3 border-b bg-gray-50 flex items-end gap-3 flex-wrap">
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">Tarih</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="manual-date"
              className="text-sm border rounded px-2 py-1 outline-none focus:border-blue-400" />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">Saat</label>
            <input type="time" value={time} onChange={e => setTime(e.target.value)} data-testid="manual-time"
              className="text-sm border rounded px-2 py-1 outline-none focus:border-blue-400" />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">Kategori</label>
            <select value={category} onChange={e => setCategory(e.target.value)} data-testid="manual-category"
              className="text-sm border rounded px-2 py-1 outline-none focus:border-blue-400 bg-white">
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-5">
          {step === "input" ? (
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">WhatsApp / OTA metnini yapistir</label>
              <textarea value={rawText} onChange={e => setRawText(e.target.value)} data-testid="manual-textarea"
                rows={12} placeholder={"GYGX7M7568G7\n3 yemeksiz 1 çocuk\nMohamed Amine Zgaren\n+33636053085\n..."}
                className="w-full text-sm border rounded-lg px-3 py-2 outline-none focus:border-blue-400 font-mono" />
            </div>
          ) : (
            <div>
              <div className="text-xs text-gray-500 mb-2">{records.length} kayit ayristirildi. Duzeltip onaylayin. Sari cerceveli satirlarda uyari var.</div>
              <div className="space-y-2">
                {records.map((r, idx) => {
                  const hasWarn = (r.uyarilar || []).length > 0;
                  return (
                    <div key={idx} data-testid={`manual-row-${idx}`}
                      className={`border rounded-lg p-2 ${hasWarn ? "border-amber-400 bg-amber-50/40" : "border-gray-200"}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        {EDIT_FIELDS.map(f => (
                          <div key={f.key} className="flex flex-col">
                            <span className="text-[9px] text-gray-400 uppercase">{f.label}</span>
                            <input value={r[f.key] ?? ""} data-testid={`manual-field-${idx}-${f.key}`}
                              onChange={e => editField(idx, f.key, e.target.value)}
                              className={`${f.w} text-xs border rounded px-1.5 py-1 outline-none focus:border-blue-400`} />
                          </div>
                        ))}
                        <div className="flex flex-col">
                          <span className="text-[9px] text-gray-400 uppercase">Pax</span>
                          <span className="text-xs font-bold px-1.5 py-1">{r.total_pax}</span>
                        </div>
                        <button data-testid={`manual-row-delete-${idx}`} onClick={() => deleteRow(idx)}
                          className="ml-auto p-1.5 rounded text-red-400 hover:bg-red-50 hover:text-red-600 self-center"><Trash2 className="w-4 h-4" /></button>
                      </div>
                      {hasWarn && (
                        <div data-testid={`manual-warning-${idx}`} className="mt-1.5 flex items-center gap-1.5 text-[11px] text-amber-700">
                          <AlertTriangle className="w-3 h-3 shrink-0" />
                          {(r.uyarilar || []).join(" · ")}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* footer */}
        <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-between gap-3">
          <div className="text-xs">
            {result && (result.error
              ? <span className="text-red-600" data-testid="manual-result">Kayit hatasi</span>
              : <span className="text-green-600 font-medium" data-testid="manual-result">{result.created} eklendi{result.failed ? `, ${result.failed} basarisiz` : ""}</span>)}
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="manual-cancel" onClick={onClose} className="text-sm px-3 py-1.5 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium">Vazgec</button>
            {step === "input" ? (
              <button data-testid="manual-preview-btn" onClick={doPreview} disabled={loading || !rawText.trim()}
                className="text-sm px-4 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white font-medium flex items-center gap-1.5 disabled:opacity-50">
                <Eye className="w-4 h-4" /> {loading ? "Ayristiriliyor..." : "Onizle"}
              </button>
            ) : (
              <>
                <button data-testid="manual-back-btn" onClick={() => setStep("input")} className="text-sm px-3 py-1.5 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium">Geri</button>
                <button data-testid="manual-confirm-btn" onClick={doConfirm} disabled={loading || !records.length}
                  className="text-sm px-4 py-1.5 rounded bg-green-600 hover:bg-green-700 text-white font-medium flex items-center gap-1.5 disabled:opacity-50">
                  <Check className="w-4 h-4" /> {loading ? "Ekleniyor..." : "Onayla ve deftere ekle"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
